import { ChevronDownIcon } from "Icons/ChevronDown";
import { ChevronUpIcon } from "Icons/ChevronUp";
import { Stars01FilledIcon } from "Icons/Stars01Filled";
import { TrendUp01Icon } from "Icons/TrendUp01";
import { keyBy } from "lodash";

import { MutableRefObject, useState } from "react";

import { css } from "@emotion/react";

import { Button } from "components/shared/library/Button";
import { OptionsMenu } from "components/shared/library/OptionsMenu";
import { Portal } from "components/shared/library/Portal";
import { Typography } from "components/shared/library/Typography";

import { useJobSearch } from "utils/jobsSearch";
import { useCloseOnClickOutside } from "utils/libraryHooks";
import { isViewMobile } from "utils/mediaQueryStrings";
import { useElementPostion } from "utils/portal";

import { uiGray } from "constants/colors";

const getStyle = (positionRight: number, positionTop: number) => ({
  wrapper: css({
    width: 190,
  }),
  button: css({
    whiteSpace: "nowrap",
  }),
  optionsWrapper: css({
    position: "absolute",
    top: positionTop + 30,
    left: positionRight - 160,
    width: 160,
  }),
  optionComponent: css({
    display: "flex",
    gap: 8,
  }),
});

type Props = {
  parentDivRef?: MutableRefObject<HTMLDivElement | null>;
};

export const JobTypeFilter = ({ parentDivRef }: Props) => {
  const [showOptions, setShowOptions] = useState(false);
  const { upsertJobSearchFilter, jobSearch } = useJobSearch();
  const options = [
    {
      name: "forYou",
      label: "Recommended",
      Icon: Stars01FilledIcon,
    },
    {
      name: "all",
      label: "All",
      Icon: TrendUp01Icon,
    },
  ];
  const optionsMap = keyBy(options, "name");
  const tailored = jobSearch.search?.filters?.tailored || "forYou";
  const { right, top, ref } = useElementPostion({
    parentDiv: parentDivRef?.current,
    callBack: () => {
      if (isViewMobile()) {
        setShowOptions(false);
      }
    },
  });
  const style = getStyle(right, top);

  const buttonId = "dropdown-filter-button-Recommended";
  const listId = "dropdown-filter-list-Recommended";

  useCloseOnClickOutside(showOptions, setShowOptions, listId, {
    exceptSelectors: [`#${buttonId}`],
  });

  return (
    <>
      <Button
        id={buttonId}
        ariaLabel="Tailored"
        buttonRef={ref as React.RefObject<HTMLButtonElement>}
        onClick={() => {
          setShowOptions((state) => !state);
        }}
        IconLeft={optionsMap[tailored].Icon}
        IconRight={showOptions ? ChevronUpIcon : ChevronDownIcon}
        color={tailored === "all" ? "gray" : "black"}
        size={"small"}
      >
        {optionsMap[tailored].label}
      </Button>

      <Portal>
        <div css={style.optionsWrapper} id={listId}>
          <OptionsMenu
            options={options}
            onChange={(option) => {
              upsertJobSearchFilter(
                "tailored",
                option.name as "forYou" | "all"
              );
              setShowOptions(false);
            }}
            isVisible={showOptions}
            OptionComponent={({ label, Icon }) => (
              <div css={style.optionComponent}>
                <Icon stroke={uiGray[70]} width={20} height={20} />
                <Typography color={uiGray[80]} weight="medium" size="XS">
                  {label}
                </Typography>
              </div>
            )}
          />
        </div>
      </Portal>
    </>
  );
};
